import { c as _c } from "react/compiler-runtime";
import { IcoCheck16, IcoClose16, IcoPlusSmall16, IcoSparkles16, IcoTrash16, IcoWarningSmall16 } from '@onefootprint/icons';
import { getLogger } from '@onefootprint/logger';
import type { AgentSuggestionOp } from '@onefootprint/request-types/dashboard';
import { IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { agentNodeName, byAgentNodeKind } from '@/utils/agent-node';
import { useBuilder } from '../../store/provider';
import { useIsSuggestionFocused } from '../../store/suggestions-nav';
import type { BundleNode, Suggestion } from '../../store/types';
import { nodeName } from '../../store/types';
const {
  logWarn
} = getLogger({
  location: 'agent-builder-change-row'
});
type RowAction = 'add' | 'remove' | 'edit';
type RowMeta = {
  action: RowAction;
  title: string;
  target?: string;
};
type ChangeRowProps = {
  suggestion: Suggestion;
  node?: BundleNode;
  onSelect: (suggestion: Suggestion) => void;
  // Chained child of a pending add_node: indented, and not flagged "out of date" — it can't apply
  // alone but its parent ships first in the same batch.
  nested?: boolean;
  // For rows inside the "Out of date" section, where the group header already says it.
  hideStaleChip?: boolean;
};
const ChangeRow = t0 => {
  const $ = _c(64);
  const {
    suggestion,
    node,
    onSelect,
    nested: t1,
    hideStaleChip: t2
  } = t0;
  const nested = t1 === undefined ? false : t1;
  const hideStaleChip = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "agentBuilder.changes"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t3);
  const {
    acceptSuggestions,
    rejectSuggestions,
    isSavingGraph,
    isReadOnly
  } = useBuilder();
  const isFocused = useIsSuggestionFocused(suggestion.id);
  const meta = useRowMeta(suggestion.operation, node);
  const isStale = !suggestion.canApply;
  const titleId = useId();
  const t4 = isFocused ? "border-accent bg-accent/5" : "border-transparent hover:bg-hover";
  let t5;
  if ($[1] !== t4) {
    t5 = cx("group relative flex items-stretch border-l-2 transition-colors", t4);
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== onSelect || $[4] !== suggestion) {
    t6 = () => onSelect(suggestion);
    $[3] = onSelect;
    $[4] = suggestion;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = nested ? "pl-9" : "pl-3.5";
  let t8;
  if ($[6] !== t7) {
    t8 = cx("flex-1 min-w-0 flex items-start gap-2.5 pr-1 py-2.5 text-left cursor-pointer", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== meta.action) {
    t9 = <KindGlyph action={meta.action} />;
    $[8] = meta.action;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== meta.title) {
    t10 = <span className="text-body-3 text-primary truncate">{meta.title}</span>;
    $[10] = meta.title;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== meta.target) {
    t11 = meta.target && <span className="text-snippet-3 text-tertiary truncate">{meta.target}</span>;
    $[12] = meta.target;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== titleId) {
    t12 = <div id={titleId} className="flex items-baseline gap-1.5 min-w-0">{t10}{t11}</div>;
    $[14] = t10;
    $[15] = t11;
    $[16] = titleId;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== suggestion.description) {
    t13 = suggestion.description && <p className="text-caption-3 text-tertiary line-clamp-2">{suggestion.description}</p>;
    $[18] = suggestion.description;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== hideStaleChip || $[21] !== isStale || $[22] !== nested || $[23] !== t) {
    t14 = isStale && !nested && !hideStaleChip && <span className="flex items-center gap-1 text-caption-4 text-warning"><IcoWarningSmall16 color="warning" className="shrink-0" />{t(_temp)}</span>;
    $[20] = hideStaleChip;
    $[21] = isStale;
    $[22] = nested;
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t13 || $[27] !== t14) {
    t15 = <div className="flex-1 min-w-0 flex flex-col gap-0.5">{t12}{t13}{t14}</div>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t6 || $[31] !== t8 || $[32] !== t9) {
    t16 = <button type="button" tabIndex={0} onClick={t6} className={t8}>{t9}{t15}</button>;
    $[29] = t15;
    $[30] = t6;
    $[31] = t8;
    $[32] = t9;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = !isFocused && "md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100";
  let t18;
  if ($[34] !== t17) {
    t18 = cx("flex items-center gap-1 pr-2 self-center shrink-0 transition-opacity md:gap-0.5", t17);
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t) {
    t19 = t(_temp2);
    $[36] = t;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = isStale || isSavingGraph || isReadOnly;
  let t21;
  if ($[38] !== acceptSuggestions || $[39] !== suggestion.id) {
    t21 = () => acceptSuggestions([suggestion.id]);
    $[38] = acceptSuggestions;
    $[39] = suggestion.id;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t19 || $[42] !== t20 || $[43] !== t21 || $[44] !== titleId) {
    t22 = <IconButton aria-label={t19} aria-describedby={titleId} icon={IcoCheck16} size="tiny" variant="ghost" className="max-md:size-9" disabled={t20} onClick={t21} />;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
    $[44] = titleId;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t) {
    t23 = t(_temp3);
    $[46] = t;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const t24 = isSavingGraph || isReadOnly;
  let t25;
  if ($[48] !== rejectSuggestions || $[49] !== suggestion.id) {
    t25 = () => rejectSuggestions([suggestion.id]);
    $[48] = rejectSuggestions;
    $[49] = suggestion.id;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t23 || $[52] !== t24 || $[53] !== t25 || $[54] !== titleId) {
    t26 = <IconButton aria-label={t23} aria-describedby={titleId} icon={IcoClose16} size="tiny" variant="ghost" className="max-md:size-9" disabled={t24} onClick={t25} />;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = titleId;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t18 || $[57] !== t22 || $[58] !== t26) {
    t27 = <div className={t18}>{t22}{t26}</div>;
    $[56] = t18;
    $[57] = t22;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t16 || $[61] !== t27 || $[62] !== t5) {
    t28 = <li className={t5}>{t16}{t27}</li>;
    $[60] = t16;
    $[61] = t27;
    $[62] = t5;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  return t28;
};
const GLYPH: Record<RowAction, typeof IcoSparkles16> = {
  add: IcoPlusSmall16,
  remove: IcoTrash16,
  edit: IcoSparkles16
};
const KindGlyph = t0 => {
  const $ = _c(2);
  const {
    action
  } = t0;
  const Icon = GLYPH[action];
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon color="tertiary" className="shrink-0 mt-0.5" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const useRowMeta = (op, node) => {
  const $ = _c(113);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.changes.rows"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  let t1;
  if ($[1] !== node) {
    t1 = key => (node?.kind === "task" ? node.artifacts.find(a => a.id === key)?.name : undefined) || key;
    $[1] = node;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const artifactName = t1;
  if (op.kind === "update_inputs") {
    let t2;
    if ($[3] !== t) {
      t2 = t(_temp4);
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = {
        action: "edit",
        title: t2
      };
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (op.kind === "update_outputs") {
    let t2;
    if ($[7] !== t) {
      t2 = t(_temp5);
      $[7] = t;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== t2) {
      t3 = {
        action: "edit",
        title: t2
      };
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (op.kind === "update_norms") {
    let t2;
    if ($[11] !== t) {
      t2 = t(_temp6);
      $[11] = t;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t2) {
      t3 = {
        action: "edit",
        title: t2
      };
      $[13] = t2;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  if (op.kind === "update_skills") {
    let t2;
    if ($[15] !== t) {
      t2 = t(_temp7);
      $[15] = t;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    let t3;
    if ($[17] !== t2) {
      t3 = {
        action: "edit",
        title: t2
      };
      $[17] = t2;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    return t3;
  }
  if (op.kind === "add_node") {
    let t2;
    if ($[19] !== op.node || $[20] !== t) {
      t2 = op.node.kind === "decision" ? t(_temp8) : byAgentNodeKind(op.node, t(_temp9), t(_temp10), t(_temp11));
      $[19] = op.node;
      $[20] = t;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    const title = t2;
    let t3;
    if ($[22] !== op.node) {
      t3 = agentNodeName(op.node) || undefined;
      $[22] = op.node;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    let t4;
    if ($[24] !== t3 || $[25] !== title) {
      t4 = {
        action: "add",
        title,
        target: t3
      };
      $[24] = t3;
      $[25] = title;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    return t4;
  }
  if (op.kind === "remove_node") {
    if (!node) {
      let t2;
      if ($[27] !== t) {
        t2 = t(_temp12);
        $[27] = t;
        $[28] = t2;
      } else {
        t2 = $[28];
      }
      let t3;
      if ($[29] !== t2) {
        t3 = {
          action: "remove",
          title: t2
        };
        $[29] = t2;
        $[30] = t3;
      } else {
        t3 = $[30];
      }
      return t3;
    }
    let t2;
    if ($[31] !== node.kind || $[32] !== t) {
      t2 = node.kind === "rules" ? t(_temp13) : node.kind === "decision" ? t(_temp14) : t(_temp15);
      $[31] = node.kind;
      $[32] = t;
      $[33] = t2;
    } else {
      t2 = $[33];
    }
    const title_0 = t2;
    let t3;
    if ($[34] !== node) {
      t3 = nodeName(node);
      $[34] = node;
      $[35] = t3;
    } else {
      t3 = $[35];
    }
    let t4;
    if ($[36] !== t3 || $[37] !== title_0) {
      t4 = {
        action: "remove",
        title: title_0,
        target: t3
      };
      $[36] = t3;
      $[37] = title_0;
      $[38] = t4;
    } else {
      t4 = $[38];
    }
    return t4;
  }
  if (op.kind === "update_node") {
    let t2;
    if ($[39] !== op.node || $[40] !== t) {
      t2 = op.node.kind === "decision" ? t(_temp16) : byAgentNodeKind(op.node, t(_temp17), t(_temp18), t(_temp19));
      $[39] = op.node;
      $[40] = t;
      $[41] = t2;
    } else {
      t2 = $[41];
    }
    const title_1 = t2;
    let t3;
    if ($[42] !== op.node) {
      t3 = agentNodeName(op.node) || undefined;
      $[42] = op.node;
      $[43] = t3;
    } else {
      t3 = $[43];
    }
    let t4;
    if ($[44] !== t3 || $[45] !== title_1) {
      t4 = {
        action: "edit",
        title: title_1,
        target: t3
      };
      $[44] = t3;
      $[45] = title_1;
      $[46] = t4;
    } else {
      t4 = $[46];
    }
    return t4;
  }
  if (op.kind === "update_task_node_meta") {
    let t2;
    if ($[47] !== t) {
      t2 = t(_temp20);
      $[47] = t;
      $[48] = t2;
    } else {
      t2 = $[48];
    }
    let t3;
    if ($[49] !== node) {
      t3 = node ? nodeName(node) : undefined;
      $[49] = node;
      $[50] = t3;
    } else {
      t3 = $[50];
    }
    let t4;
    if ($[51] !== t2 || $[52] !== t3) {
      t4 = {
        action: "edit",
        title: t2,
        target: t3
      };
      $[51] = t2;
      $[52] = t3;
      $[53] = t4;
    } else {
      t4 = $[53];
    }
    return t4;
  }
  if (op.kind === "update_task_field") {
    let t2;
    if ($[54] !== t) {
      t2 = t(_temp21);
      $[54] = t;
      $[55] = t2;
    } else {
      t2 = $[55];
    }
    const t3 = op.newKey ?? op.key;
    let t4;
    if ($[56] !== t2 || $[57] !== t3) {
      t4 = {
        action: "edit",
        title: t2,
        target: t3
      };
      $[56] = t2;
      $[57] = t3;
      $[58] = t4;
    } else {
      t4 = $[58];
    }
    return t4;
  }
  if (op.kind === "add_task_field") {
    let t2;
    if ($[59] !== t) {
      t2 = t(_temp22);
      $[59] = t;
      $[60] = t2;
    } else {
      t2 = $[60];
    }
    let t3;
    if ($[61] !== op.field.key || $[62] !== t2) {
      t3 = {
        action: "add",
        title: t2,
        target: op.field.key
      };
      $[61] = op.field.key;
      $[62] = t2;
      $[63] = t3;
    } else {
      t3 = $[63];
    }
    return t3;
  }
  if (op.kind === "remove_task_field") {
    let t2;
    if ($[64] !== t) {
      t2 = t(_temp23);
      $[64] = t;
      $[65] = t2;
    } else {
      t2 = $[65];
    }
    let t3;
    if ($[66] !== op.key || $[67] !== t2) {
      t3 = {
        action: "remove",
        title: t2,
        target: op.key
      };
      $[66] = op.key;
      $[67] = t2;
      $[68] = t3;
    } else {
      t3 = $[68];
    }
    return t3;
  }
  if (op.kind === "update_task_artifact") {
    let t2;
    if ($[69] !== t) {
      t2 = t(_temp24);
      $[69] = t;
      $[70] = t2;
    } else {
      t2 = $[70];
    }
    const t3 = op.newKey ?? op.key;
    let t4;
    if ($[71] !== artifactName || $[72] !== t3) {
      t4 = artifactName(t3);
      $[71] = artifactName;
      $[72] = t3;
      $[73] = t4;
    } else {
      t4 = $[73];
    }
    let t5;
    if ($[74] !== t2 || $[75] !== t4) {
      t5 = {
        action: "edit",
        title: t2,
        target: t4
      };
      $[74] = t2;
      $[75] = t4;
      $[76] = t5;
    } else {
      t5 = $[76];
    }
    return t5;
  }
  if (op.kind === "add_task_artifact") {
    let t2;
    if ($[77] !== t) {
      t2 = t(_temp25);
      $[77] = t;
      $[78] = t2;
    } else {
      t2 = $[78];
    }
    const t3 = op.artifact.name || op.artifact.key;
    let t4;
    if ($[79] !== t2 || $[80] !== t3) {
      t4 = {
        action: "add",
        title: t2,
        target: t3
      };
      $[79] = t2;
      $[80] = t3;
      $[81] = t4;
    } else {
      t4 = $[81];
    }
    return t4;
  }
  if (op.kind === "remove_task_artifact") {
    let t2;
    if ($[82] !== t) {
      t2 = t(_temp26);
      $[82] = t;
      $[83] = t2;
    } else {
      t2 = $[83];
    }
    let t3;
    if ($[84] !== artifactName || $[85] !== op.key) {
      t3 = artifactName(op.key);
      $[84] = artifactName;
      $[85] = op.key;
      $[86] = t3;
    } else {
      t3 = $[86];
    }
    let t4;
    if ($[87] !== t2 || $[88] !== t3) {
      t4 = {
        action: "remove",
        title: t2,
        target: t3
      };
      $[87] = t2;
      $[88] = t3;
      $[89] = t4;
    } else {
      t4 = $[89];
    }
    return t4;
  }
  if (op.kind === "update_rules_node_meta") {
    let t2;
    if ($[90] !== t) {
      t2 = t(_temp27);
      $[90] = t;
      $[91] = t2;
    } else {
      t2 = $[91];
    }
    let t3;
    if ($[92] !== node) {
      t3 = node ? nodeName(node) : undefined;
      $[92] = node;
      $[93] = t3;
    } else {
      t3 = $[93];
    }
    let t4;
    if ($[94] !== t2 || $[95] !== t3) {
      t4 = {
        action: "edit",
        title: t2,
        target: t3
      };
      $[94] = t2;
      $[95] = t3;
      $[96] = t4;
    } else {
      t4 = $[96];
    }
    return t4;
  }
  if (op.kind === "update_rules_group") {
    let t2;
    if ($[97] !== t) {
      t2 = t(_temp28);
      $[97] = t;
      $[98] = t2;
    } else {
      t2 = $[98];
    }
    const t3 = op.value || undefined;
    let t4;
    if ($[99] !== t2 || $[100] !== t3) {
      t4 = {
        action: "edit",
        title: t2,
        target: t3
      };
      $[99] = t2;
      $[100] = t3;
      $[101] = t4;
    } else {
      t4 = $[101];
    }
    return t4;
  }
  if (op.kind === "add_rules_group") {
    let t2;
    if ($[102] !== t) {
      t2 = t(_temp29);
      $[102] = t;
      $[103] = t2;
    } else {
      t2 = $[103];
    }
    const t3 = op.group.value || undefined;
    let t4;
    if ($[104] !== t2 || $[105] !== t3) {
      t4 = {
        action: "add",
        title: t2,
        target: t3
      };
      $[104] = t2;
      $[105] = t3;
      $[106] = t4;
    } else {
      t4 = $[106];
    }
    return t4;
  }
  if (op.kind === "remove_rules_group") {
    let t2;
    if ($[107] !== t) {
      t2 = t(_temp30);
      $[107] = t;
      $[108] = t2;
    } else {
      t2 = $[108];
    }
    let t3;
    if ($[109] !== t2) {
      t3 = {
        action: "remove",
        title: t2
      };
      $[109] = t2;
      $[110] = t3;
    } else {
      t3 = $[110];
    }
    return t3;
  }
  let t2;
  if ($[111] !== op) {
    t2 = assertNeverRow(op);
    $[111] = op;
    $[112] = t2;
  } else {
    t2 = $[112];
  }
  return t2;
};
const assertNeverRow = (op: never): RowMeta => {
  logWarn('Unhandled suggestion op kind in changes row', undefined, {
    kind: (op as {
      kind?: string;
    }).kind
  });
  return {
    action: 'edit',
    title: ''
  };
};
export default ChangeRow;
function _temp($) {
  return $.stale;
}
function _temp2($_0) {
  return $_0.accept;
}
function _temp3($_1) {
  return $_1.dismiss;
}
function _temp4($) {
  return $.updateInputs;
}
function _temp5($_0) {
  return $_0.updateOutputs;
}
function _temp6($_1) {
  return $_1.updateNorms;
}
function _temp7($_2) {
  return $_2.updateSkills;
}
function _temp8($_3) {
  return $_3.addDecision;
}
function _temp9($_4) {
  return $_4.addTask;
}
function _temp10($_5) {
  return $_5.addRules;
}
function _temp11($_6) {
  return $_6.addNode;
}
function _temp12($_7) {
  return $_7.removeNode;
}
function _temp13($_8) {
  return $_8.removeRules;
}
function _temp14($_9) {
  return $_9.removeDecision;
}
function _temp15($_10) {
  return $_10.removeTask;
}
function _temp16($_11) {
  return $_11.replaceDecision;
}
function _temp17($_12) {
  return $_12.replaceTask;
}
function _temp18($_13) {
  return $_13.replaceRules;
}
function _temp19($_14) {
  return $_14.replaceNode;
}
function _temp20($_15) {
  return $_15.refineTaskMeta;
}
function _temp21($_16) {
  return $_16.refineField;
}
function _temp22($_17) {
  return $_17.addField;
}
function _temp23($_18) {
  return $_18.removeField;
}
function _temp24($_19) {
  return $_19.refineArtifact;
}
function _temp25($_20) {
  return $_20.addArtifact;
}
function _temp26($_21) {
  return $_21.removeArtifact;
}
function _temp27($_22) {
  return $_22.refineRulesMeta;
}
function _temp28($_23) {
  return $_23.refineGroup;
}
function _temp29($_24) {
  return $_24.addGroup;
}
function _temp30($_25) {
  return $_25.removeGroup;
}